/**
 * Installed-app branding (home-screen name + icon) from the default company.
 * Icons are generated in the browser from the company's logo; the web app manifest
 * is swapped for a generated blob manifest so installs pick up the company identity.
 */

export const BRANDING_STORAGE_KEY = "appBranding.v1";
const SHORT_NAME_MAX = 12;
const MAX_LOGO_BYTES = 150 * 1024;
const THEME_COLOR = "#0f172a";

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not read the image."));
    img.src = src;
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.readAsDataURL(file);
  });
}

/** Fit the image inside a size×size square (transparent padding), centred. */
function drawContained(ctx, img, size, inset) {
  const box = size * inset;
  const scale = Math.min(box / img.width, box / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
}

/**
 * Normalises an uploaded logo to a 512×512 transparent PNG data URL.
 * Throws if the result is too large to store on the company document.
 */
export async function prepareLogoFromFile(file) {
  if (!file || !file.type.startsWith("image/")) throw new Error("Please choose an image file.");
  const img = await loadImage(await readFileAsDataUrl(file));
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 512;
  drawContained(canvas.getContext("2d"), img, 512, 1);

  let dataUrl = canvas.toDataURL("image/png");
  if (dataUrl.length > MAX_LOGO_BYTES * 1.37) {
    // Photos compress badly as PNG — retry at 256px before giving up
    const small = document.createElement("canvas");
    small.width = small.height = 256;
    drawContained(small.getContext("2d"), img, 256, 1);
    dataUrl = small.toDataURL("image/png");
  }
  if (dataUrl.length > MAX_LOGO_BYTES * 1.37) {
    throw new Error("This logo is too detailed to use as an app icon. Please use a simpler logo (PNG/SVG with a plain background).");
  }
  return dataUrl;
}

/** Cheap fingerprint of everything that affects branding (detects logo/name changes). */
export function brandingKey(company) {
  const logo = String(company?.logoUrl || "");
  let hash = 5381;
  for (let i = 0; i < logo.length; i += 7) hash = ((hash * 33) ^ logo.charCodeAt(i)) >>> 0;
  return `${company?.id || ""}|${company?.name || ""}|${company?.tradeName || ""}|${company?.appShortName || ""}|${logo.length}:${hash}`;
}

/** Name under the home-screen icon: explicit appShortName, else leading words of the company name. */
export function deriveShortName(company) {
  const explicit = String(company?.appShortName || "").trim();
  if (explicit) return explicit.slice(0, SHORT_NAME_MAX);
  const words = String(company?.name || company?.tradeName || "").trim().split(/\s+/).filter(Boolean);
  let result = "";
  for (const word of words) {
    const next = result ? `${result} ${word}` : word;
    if (next.length > SHORT_NAME_MAX) break;
    result = next;
  }
  return result || words[0]?.slice(0, SHORT_NAME_MAX) || "";
}

/** Draws the logo on a white tile; maskable icons keep the logo inside the 80% safe zone. */
function renderIcon(img, size, { maskable = false, rounded = false } = {}) {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  if (rounded) {
    const r = size * 0.22;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.arcTo(size, 0, size, size, r);
    ctx.arcTo(size, size, 0, size, r);
    ctx.arcTo(0, size, 0, 0, r);
    ctx.arcTo(0, 0, size, 0, r);
    ctx.closePath();
    ctx.fill();
  } else {
    ctx.fillRect(0, 0, size, size);
  }
  drawContained(ctx, img, size, maskable ? 0.66 : 0.84);
  return canvas.toDataURL("image/png");
}

/** Builds the branding for a company (name, short name, generated icon data URLs or null). */
export async function buildBranding(company) {
  if (!company?.name) return null;
  const branding = {
    key: brandingKey(company),
    name: company.name,
    shortName: deriveShortName(company),
    icons: null
  };
  if (company.logoUrl) {
    try {
      const img = await loadImage(company.logoUrl);
      branding.icons = {
        icon192: renderIcon(img, 192, { rounded: true }),
        icon512: renderIcon(img, 512, { rounded: true }),
        maskable512: renderIcon(img, 512, { maskable: true }),
        apple180: renderIcon(img, 180),
        favicon64: renderIcon(img, 64, { rounded: true })
      };
    } catch (err) {
      console.warn("[Branding] Logo could not be rendered, using default icons:", err.message);
    }
  }
  return branding;
}

function buildManifest(branding) {
  const origin = window.location.origin;
  const icons = branding.icons
    ? [
      { src: branding.icons.icon192, sizes: "192x192", type: "image/png", purpose: "any" },
      { src: branding.icons.icon512, sizes: "512x512", type: "image/png", purpose: "any" },
      { src: branding.icons.maskable512, sizes: "512x512", type: "image/png", purpose: "maskable" }
    ]
    : [
      { src: `${origin}/icons/icon-192.png`, sizes: "192x192", type: "image/png", purpose: "any" },
      { src: `${origin}/icons/icon-512.png`, sizes: "512x512", type: "image/png", purpose: "any" },
      { src: `${origin}/icons/icon-512-maskable.png`, sizes: "512x512", type: "image/png", purpose: "maskable" }
    ];
  return {
    id: "/",
    name: branding.name,
    short_name: branding.shortName || branding.name,
    description: `${branding.name} — billing, stock and invoicing`,
    start_url: `${origin}/`,
    scope: `${origin}/`,
    display: "standalone",
    background_color: THEME_COLOR,
    theme_color: THEME_COLOR,
    orientation: "portrait-primary",
    icons
  };
}

function setLink(rel, href, attrs = {}) {
  let el = document.querySelector(`link[rel="${rel}"]${attrs.type ? `[type="${attrs.type}"]` : ""}`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.href = href;
}

let currentManifestUrl = null;

/** Applies branding to the document: manifest, icons, iOS title and page title. */
export function applyBranding(branding) {
  if (!branding?.name) return;

  if (currentManifestUrl) URL.revokeObjectURL(currentManifestUrl);
  const blob = new Blob([JSON.stringify(buildManifest(branding))], { type: "application/manifest+json" });
  currentManifestUrl = URL.createObjectURL(blob);
  setLink("manifest", currentManifestUrl);

  if (branding.icons) {
    setLink("apple-touch-icon", branding.icons.apple180);
    setLink("icon", branding.icons.favicon64, { type: "image/png" });
    document.querySelector('link[rel="icon"][type="image/svg+xml"]')?.remove();
  }

  let meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "apple-mobile-web-app-title";
    document.head.appendChild(meta);
  }
  meta.content = branding.shortName || branding.name;
  document.title = branding.name;
}

export function saveBranding(branding) {
  try {
    if (branding) localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(branding));
  } catch (_) {
    // Storage full or blocked — branding still applies for this session
  }
}

export function loadSavedBranding() {
  try {
    return JSON.parse(localStorage.getItem(BRANDING_STORAGE_KEY) || "null");
  } catch (_) {
    return null;
  }
}
